import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Plus, Edit, Trash2, ExternalLink, Loader2, Eye, EyeOff, Sparkles, Check, Bot, Settings, Clock, Zap, Globe } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { formatApiError } from '@/components/ProtectedRoute';
import { PageMeta } from '@/components/PageMeta';
import { API } from '@/lib/api';


export default function AdminBlogListPage() {
  const [posts, setPosts] = useState(null);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/blog/admin/posts`);
      setPosts(data);
    } catch (err) {
      toast.error(formatApiError(err));
      setPosts([]);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleDelete = async (slug) => {
    try {
      await axios.delete(`${API}/blog/admin/posts/${slug}`);
      toast.success('Article supprimé');
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const handlePublish = async (slug) => {
    try {
      await axios.post(`${API}/admin/blog/publish/${slug}`);
      toast.success('Brouillon publié');
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <AdminLayout>
      <PageMeta title="Blog admin" description="Gestion des articles" />
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <span className="text-primary font-semibold text-xs tracking-[0.3em] uppercase">━━ Blog</span>
          <h1 className="font-heading text-4xl md:text-5xl text-dark mt-2">
            ARTICLES <span className="text-gradient-primary italic font-display inline-block pr-2">PUBLIÉS</span>
          </h1>
        </div>
        <div className="flex flex-wrap gap-2">
          <PushIndexNowButton />
          <PushGoogleIndexingButton />
          <AutoBlogConfigDialog />
          <GenerateWithAIDialog onGenerated={load} />
          <Button onClick={() => navigate('/admin/blog/new')} className="bg-primary hover:bg-primary-light rounded-xl" data-testid="blog-new-button">
            <Plus className="h-4 w-4 mr-2" /> NOUVEL ARTICLE
          </Button>
        </div>
      </div>

      {posts === null ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 text-primary animate-spin" />
        </div>
      ) : posts.length === 0 ? (
        <Card className="p-12 text-center rounded-2xl">
          <p className="text-muted-foreground mb-4">Aucun article pour le moment.</p>
          <Button onClick={() => navigate('/admin/blog/new')} className="bg-primary hover:bg-primary-light">
            <Plus className="h-4 w-4 mr-2" /> Créer le premier article
          </Button>
        </Card>
      ) : (
        <div className="space-y-3">
          {posts.map((p) => (
            <Card key={p.id} className="p-5 rounded-2xl border-border hover:border-primary/30 transition-all" data-testid={`blog-row-${p.slug}`}>
              <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                {p.cover_image && (
                  <img loading="lazy" decoding="async" src={p.cover_image} alt="" className="w-full lg:w-32 h-32 lg:h-20 object-cover rounded-xl" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1.5">
                    {p.published ? (
                      <Badge className="bg-primary/15 text-primary hover:bg-primary/15 border-primary/30 text-[10px]">
                        <Eye className="h-3 w-3 mr-1" /> Publié
                      </Badge>
                    ) : (
                      <Badge variant="secondary" className="text-[10px]">
                        <EyeOff className="h-3 w-3 mr-1" /> Brouillon
                      </Badge>
                    )}
                    {(p.tags || []).slice(0, 2).map((t) => (
                      <Badge key={t} variant="outline" className="text-[10px]">
                        {t}
                      </Badge>
                    ))}
                  </div>
                  <h3 className="font-heading text-base text-dark tracking-wide mb-1 truncate">{p.title}</h3>
                  <p className="text-muted-foreground text-xs truncate">/{p.slug}</p>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  {!p.published && (
                    <Button
                      onClick={() => handlePublish(p.slug)}
                      size="sm"
                      className="rounded-xl bg-primary hover:bg-primary-light text-white"
                      data-testid={`blog-publish-${p.slug}`}
                    >
                      <Check className="h-3.5 w-3.5 mr-1" /> Publier
                    </Button>
                  )}
                  <a href={`/blog/${p.slug}`} target="_blank" rel="noreferrer">
                    <Button variant="outline" size="sm" className="rounded-xl">
                      <ExternalLink className="h-3.5 w-3.5" />
                    </Button>
                  </a>
                  <Link to={`/admin/blog/edit/${p.slug}`}>
                    <Button variant="outline" size="sm" className="rounded-xl" data-testid={`blog-edit-${p.slug}`}>
                      <Edit className="h-3.5 w-3.5" />
                    </Button>
                  </Link>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="outline" size="sm" className="rounded-xl text-destructive hover:bg-destructive/10 hover:text-destructive" data-testid={`blog-delete-${p.slug}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Supprimer cet article ?</AlertDialogTitle>
                        <AlertDialogDescription>
                          "{p.title}" sera définitivement supprimé. Cette action est irréversible.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Annuler</AlertDialogCancel>
                        <AlertDialogAction onClick={() => handleDelete(p.slug)} className="bg-destructive hover:bg-destructive/90">
                          Supprimer
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </AdminLayout>
  );
}

// ---------- AI Generation Dialog ----------
const SUGGESTED_TOPICS = [
  { topic: 'Top 5 mouvements de CrossFit pour perdre du ventre rapidement', kw: ['crossfit ventre plat', 'perdre du ventre crossfit'] },
  { topic: 'Comment préparer sa première compétition HYROX en Loire', kw: ['HYROX débutant', 'compétition hyrox france'] },
  { topic: 'CrossFit et nutrition : que manger avant et après un WOD', kw: ['nutrition crossfit', 'alimentation sportif crossfit'] },
  { topic: 'Retour au sport après les fêtes : plan de reprise CrossFit', kw: ['reprise crossfit', 'remise en forme janvier'] },
  { topic: 'CrossFit pour les entrepreneurs : gérer stress et énergie', kw: ['crossfit entrepreneur', 'sport dirigeant'] },
];

function GenerateWithAIDialog({ onGenerated }) {
  const [open, setOpen] = useState(false);
  const [topic, setTopic] = useState('');
  const [keywords, setKeywords] = useState('');
  const [loading, setLoading] = useState(false);

  const applySuggestion = (s) => {
    setTopic(s.topic);
    setKeywords(s.kw.join(', '));
  };

  const handleGenerate = async () => {
    if (!topic.trim() || topic.trim().length < 10) {
      toast.error('Précise un sujet (au moins 10 caractères).');
      return;
    }
    setLoading(true);
    try {
      const kwArr = keywords.split(',').map((k) => k.trim()).filter(Boolean);
      // 1) Kick off async job
      const { data: kick } = await axios.post(`${API}/admin/blog/generate`, {
        topic: topic.trim(),
        keywords: kwArr,
        save_as_draft: true,
      });
      const jobId = kick.job_id;
      if (!jobId) throw new Error('Pas de job_id retourné');

      // 2) Poll up to 4 minutes (every 5s)
      const start = Date.now();
      const TIMEOUT_MS = 4 * 60 * 1000;
      let finalJob = null;
      while (Date.now() - start < TIMEOUT_MS) {
        await new Promise((r) => setTimeout(r, 5000));
        const { data: job } = await axios.get(`${API}/admin/blog/generate/status/${jobId}`);
        if (job.status === 'done') { finalJob = job; break; }
        if (job.status === 'error') throw new Error(job.error || 'Erreur de génération');
      }
      if (!finalJob) throw new Error('Génération expirée (>4 min). Réessaie.');

      const post = finalJob.post || {};
      toast.success(`Brouillon créé : "${(post.title || '').slice(0, 50)}…"`);
      setOpen(false);
      setTopic('');
      setKeywords('');
      onGenerated?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          className="rounded-xl border-primary/40 text-primary hover:bg-primary/5"
          data-testid="blog-ai-generate-button"
        >
          <Sparkles className="h-4 w-4 mr-2" /> GÉNÉRER AVEC L'IA
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-wide text-dark flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" /> Générer un article avec Claude
          </DialogTitle>
          <DialogDescription>
            L'IA rédige un article SEO complet (900-1300 mots) en français, optimisé pour CrossFit Saint-Chamond.
            L'article est créé en <strong>brouillon</strong> — tu pourras le relire et publier en 1 clic.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label htmlFor="ai-topic" className="text-dark font-semibold">Sujet de l'article</Label>
            <Textarea
              id="ai-topic"
              rows={3}
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              placeholder="Ex: Comment bien s'échauffer avant une séance de CrossFit"
              className="mt-1.5"
              data-testid="ai-topic-input"
            />
          </div>
          <div>
            <Label htmlFor="ai-keywords" className="text-dark font-semibold">Mots-clés SEO (séparés par des virgules)</Label>
            <Input
              id="ai-keywords"
              value={keywords}
              onChange={(e) => setKeywords(e.target.value)}
              placeholder="échauffement crossfit, mobility wod"
              className="mt-1.5"
              data-testid="ai-keywords-input"
            />
          </div>
          <div>
            <Label className="text-dark font-semibold text-xs uppercase tracking-wider">Suggestions</Label>
            <div className="mt-2 flex flex-wrap gap-2">
              {SUGGESTED_TOPICS.map((s, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => applySuggestion(s)}
                  className="text-xs bg-accent hover:bg-primary hover:text-white text-primary px-3 py-1.5 rounded-full transition-colors"
                  data-testid={`ai-suggestion-${i}`}
                >
                  {s.topic.slice(0, 50)}{s.topic.length > 50 ? '…' : ''}
                </button>
              ))}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} disabled={loading}>Annuler</Button>
          <Button
            onClick={handleGenerate}
            disabled={loading}
            className="bg-primary hover:bg-primary-light text-white"
            data-testid="ai-generate-submit"
          >
            {loading ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Génération… (1-3 min)</>
            ) : (
              <><Sparkles className="h-4 w-4 mr-2" /> Générer le brouillon</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------- IndexNow push button ----------
function PushIndexNowButton() {
  const [loading, setLoading] = useState(false);

  const handlePush = async () => {
    if (!window.confirm('Notifier Bing/Yandex/Cloudflare de toutes tes URLs ? Idéal après un redéploiement.')) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/indexnow/push-all`);
      if (data.indexnow?.ok) {
        toast.success(`${data.urls_count} URLs soumises aux moteurs de recherche ✨`);
      } else {
        toast.error(`Erreur IndexNow : ${data.indexnow?.detail || 'unknown'}`);
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button
      onClick={handlePush}
      disabled={loading}
      variant="outline"
      className="rounded-xl border-primary/40"
      data-testid="indexnow-push-button"
      title="Pousse toutes les URLs du site vers Bing, Yandex et Cloudflare pour indexation accélérée"
    >
      {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Zap className="h-4 w-4 mr-2" />}
      PUSH GOOGLE/BING
    </Button>
  );
}

// ---------- Google Indexing API push button ----------
function PushGoogleIndexingButton() {
  const [loading, setLoading] = useState(false);
  const [configured, setConfigured] = useState(null);

  useEffect(() => {
    axios.get(`${API}/admin/google-indexing/status`)
      .then((r) => setConfigured(r.data.configured))
      .catch(() => setConfigured(false));
  }, []);

  const handlePush = async () => {
    if (!configured) {
      toast.error("Google Indexing API pas configurée. Fournis le JSON de la service account à l'agent.");
      return;
    }
    if (!window.confirm('Soumettre toutes les URLs à la Google Indexing API ? Quota par défaut : 200/jour.')) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/google-indexing/push-all`);
      const g = data.google || {};
      if (g.submitted > 0 && g.failed === 0) {
        toast.success(`${g.submitted}/${data.urls_count} URLs acceptées par Google ✨`);
      } else if (g.submitted > 0) {
        toast.warning(`${g.submitted} OK, ${g.failed} en erreur (quota ou config)`);
      } else {
        toast.error(`Échec Google Indexing : ${g.errors?.[0]?.detail || 'unknown'}`);
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button
      onClick={handlePush}
      disabled={loading || configured === null}
      variant="outline"
      className="rounded-xl border-primary/40"
      data-testid="google-indexing-push-button"
      title={configured ? "Google Indexing API configurée" : "Google Indexing API non configurée"}
    >
      {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Globe className="h-4 w-4 mr-2" />}
      {configured ? 'PUSH GOOGLE API' : 'GOOGLE API (OFF)'}
    </Button>
  );
}


// ---------- Auto-blog scheduler config dialog ----------
function AutoBlogConfigDialog() {
  const [open, setOpen] = useState(false);
  const [config, setConfig] = useState(null);
  const [saving, setSaving] = useState(false);
  const [newTopic, setNewTopic] = useState('');
  const [newKw, setNewKw] = useState('');
  const [generating, setGenerating] = useState(false);

  const loadConfig = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/blog/auto-config`);
      setConfig(data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  useEffect(() => {
    if (open) loadConfig();
  }, [open]);

  const toggleEnabled = async (v) => {
    try {
      await axios.put(`${API}/admin/blog/auto-config/toggle`, { enabled: v });
      setConfig({ ...config, enabled: v });
      toast.success(v ? 'Génération automatique activée' : 'Génération automatique désactivée');
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const addTopic = () => {
    if (!newTopic.trim()) return;
    const kw = newKw.split(',').map((k) => k.trim()).filter(Boolean);
    setConfig({ ...config, queue: [...(config.queue || []), { topic: newTopic.trim(), kw }] });
    setNewTopic('');
    setNewKw('');
  };

  const removeTopic = (idx) => {
    const q = [...(config.queue || [])];
    q.splice(idx, 1);
    setConfig({ ...config, queue: q });
  };

  const saveQueue = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/blog/auto-config/queue`, { topics: config.queue });
      toast.success('File d\'attente enregistrée');
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const generateNow = async () => {
    setGenerating(true);
    try {
      await axios.post(`${API}/admin/blog/auto-generate-now`);
      toast.success('Brouillon généré. Consulte la liste des articles.');
      setOpen(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          className="rounded-xl border-primary/40"
          data-testid="blog-auto-config-button"
        >
          <Bot className="h-4 w-4 mr-2" /> AUTO HEBDO
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-wide text-dark flex items-center gap-2">
            <Bot className="h-5 w-5 text-primary" /> Génération automatique hebdomadaire
          </DialogTitle>
          <DialogDescription>
            L'IA génère <strong>1 article par semaine</strong> à partir de ta file d'attente. L'article est créé en <strong>brouillon</strong>.
            Tu reçois un email pour le relire et le publier en 1 clic.
          </DialogDescription>
        </DialogHeader>

        {!config ? (
          <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        ) : (
          <div className="space-y-5">
            <div className="flex items-center justify-between p-4 rounded-xl bg-muted/50">
              <div>
                <Label className="text-dark font-semibold">Génération automatique active</Label>
                <p className="text-xs text-muted-foreground mt-1">
                  {config.last_run ? (
                    <>Dernier run : {new Date(config.last_run).toLocaleString('fr-FR')}</>
                  ) : (
                    'Jamais exécuté'
                  )}
                </p>
              </div>
              <Switch checked={!!config.enabled} onCheckedChange={toggleEnabled} data-testid="auto-blog-toggle" />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-dark font-semibold flex items-center gap-1.5">
                  <Clock className="h-4 w-4 text-primary" />
                  File d'attente ({config.queue?.length || 0} sujets)
                </Label>
                <Button onClick={saveQueue} disabled={saving} size="sm" variant="outline" className="rounded-lg h-8">
                  {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <><Check className="h-3 w-3 mr-1" /> Enregistrer</>}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mb-3">
                Si la file est vide, l'IA pioche dans une liste de 15 sujets SEO evergreens.
              </p>

              <div className="space-y-2 max-h-56 overflow-y-auto pr-1">
                {(config.queue || []).map((t, i) => (
                  <div key={i} className="flex items-start gap-2 p-3 bg-background border border-border rounded-lg" data-testid={`queue-item-${i}`}>
                    <span className="text-xs bg-accent text-primary font-bold px-2 py-0.5 rounded-full mt-0.5">{i + 1}</span>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm text-dark">{t.topic}</div>
                      {t.kw?.length > 0 && (
                        <div className="text-xs text-muted-foreground mt-0.5">KW: {t.kw.join(', ')}</div>
                      )}
                    </div>
                    <button onClick={() => removeTopic(i)} className="text-muted-foreground hover:text-destructive" data-testid={`queue-remove-${i}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
                {(!config.queue || config.queue.length === 0) && (
                  <p className="text-xs text-muted-foreground italic text-center py-4">
                    File vide — l'IA utilisera les sujets par défaut.
                  </p>
                )}
              </div>

              <div className="mt-4 p-3 bg-accent/30 rounded-lg space-y-2">
                <Input
                  value={newTopic}
                  onChange={(e) => setNewTopic(e.target.value)}
                  placeholder="Nouveau sujet d'article"
                  className="text-sm"
                  data-testid="auto-new-topic"
                />
                <Input
                  value={newKw}
                  onChange={(e) => setNewKw(e.target.value)}
                  placeholder="Mots-clés (séparés par des virgules)"
                  className="text-sm"
                  data-testid="auto-new-kw"
                />
                <Button onClick={addTopic} size="sm" className="w-full bg-primary hover:bg-primary-light rounded-lg" data-testid="auto-add-topic">
                  <Plus className="h-3 w-3 mr-1" /> Ajouter à la file
                </Button>
              </div>
            </div>

            <div className="p-4 border border-dashed border-primary/40 rounded-xl text-center">
              <p className="text-xs text-muted-foreground mb-3">
                Besoin d'un nouveau brouillon tout de suite ?
              </p>
              <Button
                onClick={generateNow}
                disabled={generating}
                variant="outline"
                className="rounded-lg border-primary/40"
                data-testid="auto-generate-now"
              >
                {generating ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Génération en cours…</>
                ) : (
                  <><Sparkles className="h-4 w-4 mr-2" /> Générer un brouillon maintenant</>
                )}
              </Button>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Fermer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

